import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ShopDetailModal = ({ 
  shop = null, 
  isOpen = false, 
  onClose = () => {},
  onGetDirections = () => {},
  onCallShop = () => {},
  userLocation = null
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  if (!isOpen || !shop) return null;

  const calculateDistance = (shopCoords) => {
    if (!userLocation) return 0;
    
    const R = 3959; // Earth's radius in miles
    const dLat = (shopCoords?.lat - userLocation?.lat) * Math.PI / 180;
    const dLng = (shopCoords?.lng - userLocation?.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(userLocation?.lat * Math.PI / 180) * Math.cos(shopCoords?.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getShopHours = () => {
    const today = new Date()?.getDay();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayHours = shop?.hours?.[days?.[today]];
    
    if (!todayHours || todayHours === 'closed') {
      return { status: 'closed', text: 'Closed today' };
    }
    
    const now = new Date();
    const currentTime = now?.getHours() * 60 + now?.getMinutes();
    const [openTime, closeTime] = todayHours?.split('-')?.map(time => {
      const [hours, minutes] = time?.trim()?.split(':')?.map(Number);
      return hours * 60 + (minutes || 0);
    });
    
    if (currentTime >= openTime && currentTime <= closeTime) {
      return { status: 'open', text: `Open until ${todayHours?.split('-')?.[1]?.trim()}` };
    } else if (currentTime < openTime) {
      return { status: 'closed', text: `Opens at ${todayHours?.split('-')?.[0]?.trim()}` };
    } else {
      return { status: 'closed', text: 'Closed' };
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'services', label: 'Services', icon: 'Wrench' },
    { id: 'reviews', label: 'Reviews', icon: 'Star' },
    { id: 'contact', label: 'Contact', icon: 'Phone' }
  ];

  const distance = calculateDistance(shop?.coordinates);
  const hours = getShopHours();

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Images Gallery */}
      {shop?.images && shop?.images?.length > 0 && (
        <div>
          <div className="aspect-video w-full mb-4 overflow-hidden rounded-lg">
            <Image
              src={shop?.images?.[selectedImageIndex]}
              alt={`${shop?.name} - Image ${selectedImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
          {shop?.images?.length > 1 && (
            <div className="flex space-x-2 overflow-x-auto">
              {shop?.images?.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                    selectedImageIndex === index ? 'border-accent' : 'border-transparent'
                  }`}
                >
                  <Image
                    src={image}
                    alt={`${shop?.name} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Description */}
      <div>
        <h4 className="font-semibold text-foreground mb-2">About</h4>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {shop?.description || `${shop?.name} is a full-service bike shop offering professional repairs, quality parts, and expert advice. Our experienced mechanics are passionate about cycling and committed to keeping you on the road.`}
        </p>
      </div>

      {/* Specialties */}
      {shop?.specialties && shop?.specialties?.length > 0 && (
        <div>
          <h4 className="font-semibold text-foreground mb-2">Specialties</h4>
          <div className="flex flex-wrap gap-2">
            {shop?.specialties?.map((specialty) => (
              <span
                key={specialty}
                className="bg-accent/10 text-accent text-sm px-3 py-1 rounded-full"
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Brands */}
      {shop?.brands && shop?.brands?.length > 0 && (
        <div>
          <h4 className="font-semibold text-foreground mb-2">Brands Carried</h4>
          <div className="grid grid-cols-2 gap-2">
            {shop?.brands?.slice(0, 8)?.map((brand) => (
              <div key={brand} className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Check" size={14} color="var(--color-success)" />
                <span>{brand}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ServicesTab = () => (
    <div className="space-y-4">
      {shop?.serviceDetails ? shop?.serviceDetails?.map((service) => (
        <div key={service?.name} className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-foreground">{service?.name}</h4>
            {service?.price && (
              <span className="text-accent font-medium">{service?.price}</span>
            )}
          </div>
          <p className="text-muted-foreground text-sm mb-2">{service?.description}</p>
          {service?.duration && (
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>Typical duration: {service?.duration}</span>
            </div>
          )}
        </div>
      )) : (
        <div className="grid grid-cols-1 gap-3">
          {shop?.services?.map((service) => (
            <div key={service} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
              <Icon name="Check" size={16} color="var(--color-success)" />
              <span className="text-foreground capitalize">{service?.replace('-', ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const ReviewsTab = () => (
    <div className="space-y-4">
      {/* Rating Summary */}
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center space-x-4 mb-3">
          <div className="text-3xl font-bold text-foreground">{shop?.rating}</div>
          <div>
            <div className="flex items-center space-x-1 mb-1">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <Icon
                  key={star}
                  name="Star"
                  size={16}
                  color={star <= Math.floor(shop?.rating) ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
                />
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{shop?.reviewCount} reviews</p>
          </div>
        </div>
      </div>

      {/* Recent Reviews */}
      {shop?.recentReviews ? shop?.recentReviews?.map((review) => (
        <div key={review?.id} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h5 className="font-medium text-foreground">{review?.author}</h5>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5]?.map((star) => (
                  <Icon
                    key={star}
                    name="Star"
                    size={12}
                    color={star <= review?.rating ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
                  />
                ))}
              </div>
            </div>
            <span className="text-xs text-muted-foreground">{review?.date}</span>
          </div>
          <p className="text-sm text-muted-foreground">{review?.comment}</p>
        </div>
      )) : (
        <div className="text-center py-8">
          <Icon name="MessageCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No reviews available yet.</p>
        </div>
      )}
    </div>
  );

  const ContactTab = () => (
    <div className="space-y-6">
      {/* Contact Info */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="MapPin" size={20} color="var(--color-accent)" />
          <div>
            <p className="font-medium text-foreground">Address</p>
            <p className="text-sm text-muted-foreground">{shop?.address}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Icon name="Phone" size={20} color="var(--color-accent)" />
          <div>
            <p className="font-medium text-foreground">Phone</p>
            <p className="text-sm text-muted-foreground">{shop?.phone}</p>
          </div>
        </div>

        {shop?.website && (
          <div className="flex items-center space-x-3">
            <Icon name="Globe" size={20} color="var(--color-accent)" />
            <div>
              <p className="font-medium text-foreground">Website</p>
              <a 
                href={shop?.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-accent hover:underline"
              >
                {shop?.website}
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Hours */}
      <div>
        <h4 className="font-semibold text-foreground mb-3">Hours</h4>
        <div className="space-y-2">
          {Object.entries(shop?.hours)?.map(([day, hours]) => (
            <div key={day} className="flex justify-between items-center">
              <span className="text-sm text-foreground capitalize">{day}</span>
              <span className="text-sm text-muted-foreground">
                {hours === 'closed' ? 'Closed' : hours}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="h-48 rounded-lg overflow-hidden">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title={shop?.name}
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${shop?.coordinates?.lat},${shop?.coordinates?.lng}&z=15&output=embed`}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="fixed inset-4 lg:inset-8 bg-card border border-border rounded-lg shadow-lg animate-scale-in">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-xl font-bold text-foreground">{shop?.name}</h2>
                {shop?.isPartner && (
                  <span className="bg-success text-success-foreground text-xs px-2 py-1 rounded-full font-medium">
                    Partner
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={14} />
                  <span>{distance?.toFixed(1)} mi away</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} color="var(--color-warning)" />
                  <span>{shop?.rating} ({shop?.reviewCount})</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon 
                    name="Clock" 
                    size={14} 
                    color={hours?.status === 'open' ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
                  />
                  <span className={hours?.status === 'open' ? 'text-success' : 'text-muted-foreground'}>
                    {hours?.text}
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors interactive-scale ${
                  activeTab === tab?.id
                    ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'services' && <ServicesTab />}
            {activeTab === 'reviews' && <ReviewsTab />}
            {activeTab === 'contact' && <ContactTab />}
          </div>

          {/* Footer Actions */}
          <div className="flex space-x-3 p-6 border-t border-border">
            <Button
              variant="outline"
              iconName="Navigation"
              iconPosition="left"
              onClick={() => onGetDirections(shop)}
              className="flex-1"
            >
              Get Directions
            </Button>
            <Button
              variant="default"
              iconName="Phone"
              iconPosition="left"
              onClick={() => onCallShop(shop)}
              className="flex-1"
            >
              Call Shop
            </Button>
            {shop?.website && (
              <Button
                variant="ghost"
                iconName="ExternalLink"
                onClick={() => window.open(shop?.website, '_blank')}
              >
                Website
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDetailModal;